import React, { useState, useEffect } from 'react';
import {
  Grid,
  CircularProgress,
  TextField as Input,
  Typography,
  Box,
  Paper,
  Container,
  Fade,
  Avatar,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Alert,
  Button as MuiButton,
} from '@mui/material';

import { withRouter } from 'react-router-dom';
import { LockOutlined, EmailOutlined, Visibility, VisibilityOff, VpnKeyOutlined, CheckCircle, PhoneOutlined } from '@mui/icons-material';

// styles
import useStyles from './styles';

// context
import { useUserDispatch, receiveToken, doInit } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';

//components
import { Button } from '../../components/Wrappers';

// logo
// import logo from './logo.png';
const logo = (process.env.PUBLIC_URL || '') + '/logoonly.jpeg';

const getGreeting = () => {
  const d = new Date();
  if (d.getHours() >= 4 && d.getHours() <= 12) {
    return 'Good Morning';
  } else if (d.getHours() >= 13 && d.getHours() <= 16) {
    return 'Good Day';
  } else if (d.getHours() >= 17 && d.getHours() <= 23) {
    return 'Good Evening';
  } else {
    return 'Good Night';
  }
};

function Login(props) {
  let classes = useStyles();

  // global
  let userDispatch = useUserDispatch();
  const auth = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(props.location.search);
    const token = params.get('token');

    if (token) {
      receiveToken(token, userDispatch, props.history);
      doInit()(userDispatch);
    }
  }, []); // eslint-disable-line

  // local
  let [isLoading, setIsLoading] = useState(false);
  let [error, setError] = useState(null);
  let [loginValue, setLoginValue] = useState('');
  let [passwordValue, setPasswordValue] = useState('');
  let [showPassword, setShowPassword] = useState(false);
  let [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  let [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Verify OTP, 2: Set New Password
  let [forgotPasswordPhone, setForgotPasswordPhone] = useState('');
  let [forgotPasswordOtp, setForgotPasswordOtp] = useState('');
  let [newPassword, setNewPassword] = useState('');
  let [confirmPassword, setConfirmPassword] = useState('');
  let [showNewPassword, setShowNewPassword] = useState(false);
  let [showConfirmPassword, setShowConfirmPassword] = useState(false);
  let [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  let [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  let [forgotPasswordError, setForgotPasswordError] = useState(null);
  let [resendOtpLoading, setResendOtpLoading] = useState(false);
  let [resendOtpCooldown, setResendOtpCooldown] = useState(0);
  let [setPasswordOpen, setSetPasswordOpen] = useState(false);
  let [setPasswordStep, setSetPasswordStep] = useState(1); // 1: Request OTP, 2: Enter OTP
  let [setPasswordEmail, setSetPasswordEmail] = useState('');
  let [setPasswordOtp, setSetPasswordOtp] = useState('');
  let [setPasswordNewPassword, setSetPasswordNewPassword] = useState('');
  let [setPasswordConfirmPassword, setSetPasswordConfirmPassword] = useState('');
  let [showSetPasswordNewPassword, setShowSetPasswordNewPassword] = useState(false);
  let [showSetPasswordConfirmPassword, setShowSetPasswordConfirmPassword] = useState(false);
  let [setPasswordLoading, setSetPasswordLoading] = useState(false);
  let [setPasswordSuccess, setSetPasswordSuccess] = useState(false);
  let [setPasswordError, setSetPasswordError] = useState(null);
  let [resendSetPasswordOtpLoading, setResendSetPasswordOtpLoading] = useState(false);
  let [resendSetPasswordOtpCooldown, setResendSetPasswordOtpCooldown] = useState(0);
  let [setPasswordRequestId, setSetPasswordRequestId] = useState('');
  let [setPasswordAccessToken, setSetPasswordAccessToken] = useState('');

  // Countdown timer for resend OTP
  useEffect(() => {
    let interval = null;
    if (resendOtpCooldown > 0) {
      interval = setInterval(() => {
        setResendOtpCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendOtpCooldown]);

  // Countdown timer for resend Set Password OTP
  useEffect(() => {
    let interval = null;
    if (resendSetPasswordOtpCooldown > 0) {
      interval = setInterval(() => {
        setResendSetPasswordOtpCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendSetPasswordOtpCooldown]);

  const handleLogin = async () =>
    handleLoginInner({
      auth,
      userDispatch,
      loginValue,
      passwordValue,
      history: props.history,
      setIsLoading,
      setError,
    });

  let isLoginFormValid = () => {
    return loginValue.length !== 0 && passwordValue.length !== 0;
  };

  let loginOnEnterKey = async (event) => {
    if (event.key === 'Enter' && isLoginFormValid()) {
      await handleLogin();
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleOpenForgotPassword = () => {
    setForgotPasswordOpen(true);
    setForgotPasswordStep(1);
    setForgotPasswordPhone('');
    setForgotPasswordOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError(null);
    setResendOtpLoading(false);
    setResendOtpCooldown(0);
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordStep(1);
    setForgotPasswordPhone('');
    setForgotPasswordOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setForgotPasswordSuccess(false);
    setForgotPasswordError(null);
    setResendOtpLoading(false);
    setResendOtpCooldown(0);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setForgotPasswordError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setForgotPasswordError('The new password and confirmation password must match. Please try again.');
      return;
    }

    setForgotPasswordLoading(true);
    setForgotPasswordError(null);
    setForgotPasswordSuccess(false);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/change-password/forgot-password-verify-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: forgotPasswordPhone,
          otp: forgotPasswordOtp,
          newPassword: newPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to reset password');
      }

      setForgotPasswordSuccess(true);
      setTimeout(() => {
        handleCloseForgotPassword();
      }, 2000);
    } catch (err) {
      setForgotPasswordError(err?.message || 'Failed to reset password. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!forgotPasswordPhone || forgotPasswordPhone.length < 10) {
      setForgotPasswordError('Please enter a valid phone number first');
      return;
    }

    if (resendOtpCooldown > 0) {
      return;
    }

    setResendOtpLoading(true);
    setForgotPasswordError(null);
    setForgotPasswordOtp('');

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/change-password/forgot-password-send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: forgotPasswordPhone }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to send OTP');
      }

      // Set cooldown to 60 seconds
      setResendOtpCooldown(60);
      if (forgotPasswordStep === 1) {
        setForgotPasswordStep(2);
      }
      setForgotPasswordError(null);
    } catch (err) {
      setForgotPasswordError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setResendOtpLoading(false);
    }
  };

  const handleOpenSetPassword = () => {
    setSetPasswordOpen(true);
    setSetPasswordStep(1);
    setSetPasswordEmail('');
    setSetPasswordOtp('');
    setSetPasswordNewPassword('');
    setSetPasswordConfirmPassword('');
    setShowSetPasswordNewPassword(false);
    setShowSetPasswordConfirmPassword(false);
    setSetPasswordSuccess(false);
    setSetPasswordError(null);
    setResendSetPasswordOtpLoading(false);
    setResendSetPasswordOtpCooldown(0);
  };

  const handleCloseSetPassword = () => {
    setSetPasswordOpen(false);
    setSetPasswordStep(1);
    setSetPasswordEmail('');
    setSetPasswordOtp('');
    setSetPasswordNewPassword('');
    setSetPasswordConfirmPassword('');
    setShowSetPasswordNewPassword(false);
    setShowSetPasswordConfirmPassword(false);
    setSetPasswordSuccess(false);
    setSetPasswordError(null);
    setResendSetPasswordOtpLoading(false);
    setResendSetPasswordOtpCooldown(0);
    setSetPasswordRequestId('');
    setSetPasswordAccessToken('');
  };

  const handleVerifySetPasswordOtp = async () => {
    if (setPasswordStep === 1) {
      if (!setPasswordEmail || !setPasswordEmail.includes('@')) {
        setSetPasswordError('Please enter a valid email address');
        return;
      }
      setSetPasswordLoading(true);
      setSetPasswordError(null);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/setPassword`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: setPasswordEmail }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.status === false) {
          throw new Error(data?.message || 'Failed to send OTP');
        }
        setSetPasswordRequestId(data?.data || '');
        setSetPasswordStep(2);
      } catch (err) {
        setSetPasswordError(err?.message || 'Failed to send OTP. Please try again.');
      } finally {
        setSetPasswordLoading(false);
      }
      return;
    }

    if (setPasswordStep === 2) {
      if (!setPasswordRequestId) {
        setSetPasswordError('Please send OTP first.');
        return;
      }
      if (!setPasswordOtp || setPasswordOtp.length < 4) {
        setSetPasswordError('Please enter a valid OTP');
        return;
      }
      setSetPasswordLoading(true);
      setSetPasswordError(null);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/verifySetPasswordOtp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            otp: setPasswordOtp,
            key: setPasswordRequestId,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.status === false) {
          throw new Error(data?.message || 'Invalid OTP. Please try again.');
        }
        const tokenFromOtp = data?.data?.token || data?.data?.accessToken || '';
        if (tokenFromOtp) {
          setSetPasswordAccessToken(tokenFromOtp);
        }
        setSetPasswordStep(3);
      } catch (err) {
        setSetPasswordError(err?.message || 'Invalid OTP. Please try again.');
      } finally {
        setSetPasswordLoading(false);
      }
    }
  };

  const handleSetPassword = async () => {
    if (!setPasswordNewPassword || setPasswordNewPassword.length < 6) {
      setSetPasswordError('Password must be at least 6 characters long');
      return;
    }

    if (setPasswordNewPassword !== setPasswordConfirmPassword) {
      setSetPasswordError('The new password and confirmation password must match. Please try again.');
      return;
    }

    setSetPasswordLoading(true);
    setSetPasswordError(null);
    setSetPasswordSuccess(false);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/resetPassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(setPasswordAccessToken ? { Authorization: `Bearer ${setPasswordAccessToken}` } : {}),
        },
        body: JSON.stringify({
          password: setPasswordNewPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to set password');
      }

      setSetPasswordSuccess(true);
      setTimeout(() => {
        handleCloseSetPassword();
        window.alert('Password Changed Successfully');
        if (props?.history?.push) {
          props.history.push('/login');
        }
      }, 1500);
    } catch (err) {
      setSetPasswordError(err?.message || 'Failed to set password. Please try again.');
    } finally {
      setSetPasswordLoading(false);
    }
  };

  const handleResendSetPasswordOtp = async () => {
    if (!setPasswordEmail || !setPasswordEmail.includes('@')) {
      setSetPasswordError('Please enter a valid email address first');
      return;
    }

    if (resendSetPasswordOtpCooldown > 0) {
      return;
    }

    setResendSetPasswordOtpLoading(true);
    setSetPasswordError(null);
    setSetPasswordOtp('');

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/login/setPassword`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: setPasswordEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.status === false) {
        throw new Error(data?.message || 'Failed to resend OTP');
      }
      setSetPasswordRequestId(data?.data || '');
      setResendSetPasswordOtpCooldown(60);
      setSetPasswordError(null);
    } catch (err) {
      setSetPasswordError(err?.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResendSetPasswordOtpLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        background: `
          radial-gradient(circle at top, #fef3c7 0, rgba(254,243,199,0) 45%),
          radial-gradient(circle at bottom, #e0f2fe 0, rgba(224,242,254,0) 40%),
          linear-gradient(135deg, #fee2f2 0%, #e0f2fe 40%, #ede9fe 100%)
        `,
      }}
    >
      <Container maxWidth="sm">
        <Fade in timeout={800}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(148,163,184,0.25)',
              boxShadow:
                '0 18px 45px rgba(15,23,42,0.12), 0 0 0 1px rgba(15,23,42,0.02)',
            }}
          >
            {/* Logo & Title */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 3,
              }}
            >
              {/* <Box
                sx={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.12em',
                  borderRadius: 999,
                  px: 1.6,
                  py: 0.5,
                  mb: 2,
                  backgroundColor: 'rgba(15,23,42,0.04)',
                  color: '#6b7280',
                }}
              >
                A1 Real Estate PVT LTD
              </Box> */}

              <Box
                component="img"
                src={logo}
                alt="A1 Logo"
                sx={{
                  height: 64,
                  mb: 1.5,
                }}
              />

              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: '#0f172a',
                  textAlign: 'center',
                  mb: 0.5,
                  fontSize: { xs: '1.6rem', sm: '1.9rem' },
                }}
              >
               A1 Real Estate PVT LTD
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#6b7280',
                  textAlign: 'center',
                }}
              >
                Sign in to continue to A1 Real Estate PVT LTD
              </Typography>
            </Box>

            {/* Error Message */}
            {error && (
              <Fade in>
                <Box
                  sx={{
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: 2,
                    py: 1.5,
                    px: 2,
                    mb: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#b91c1c',
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                    }}
                  >
                    {typeof error === 'string' ? error : 'Something is wrong with your login or password.'}
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* Form Fields */}
            <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Input
                id="email"
                value={loginValue}
                onChange={(e) => setLoginValue(e.target.value)}
                margin="normal"
                label="Login ID"
                placeholder="Enter your login ID"
                type="text"
                fullWidth
                variant="outlined"
                onKeyDown={(e) => loginOnEnterKey(e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(248,250,252,0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(148,163,184,0.6)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#6366f1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6366f1',
                      borderWidth: 1.5,
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.95rem',
                  },
                }}
              />

              <Input
                id="password"
                value={passwordValue}
                onChange={(e) => setPasswordValue(e.target.value)}
                margin="normal"
                label="Password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                variant="outlined"
                onKeyDown={(e) => loginOnEnterKey(e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlined sx={{ color: '#9ca3af' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: '#9ca3af' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(248,250,252,0.9)',
                    '& fieldset': {
                      borderColor: 'rgba(148,163,184,0.6)',
                    },
                    '&:hover fieldset': {
                      borderColor: '#6366f1',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#6366f1',
                      borderWidth: 1.5,
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.95rem',
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0 }}>
                {/* <Link
                  component="button"
                  variant="body2"
                  onClick={handleOpenSetPassword}
                  sx={{
                    color: '#667eea',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Set Password */}
                {/* </Link> */}
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleOpenForgotPassword}
                  sx={{
                    color: '#667eea',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Forgot Password?
                </Link>
              </Box>
            </Box>

            {/* Login Button */}
            <Box sx={{ mb: 2.5 }}>
              <Button
                disabled={isLoading || !isLoginFormValid()}
                onClick={handleLogin}
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  borderRadius: 999,
                  py: 1.3,
                  fontSize: '0.98rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#111827',
                  color: '#ffffff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  boxShadow: '0 18px 35px rgba(15,23,42,0.35)',
                  '&:hover': {
                    backgroundColor: '#020617',
                    boxShadow: '0 22px 45px rgba(15,23,42,0.45)',
                  },
                  '&:disabled': {
                    backgroundColor: '#e5e7eb',
                    boxShadow: 'none',
                  },
                }}
              >
                {isLoading && (
                  <CircularProgress
                    size={18}
                    sx={{
                      mr: 1,
                      color: '#ffffff',
                    }}
                  />
                )}
                <span
                  style={{
                    color:
                      isLoading || !isLoginFormValid()
                        ? '#9ca3af'
                        : '#ffffff',
                  }}
                >
                  {isLoading ? 'Signing in…' : 'Sign In'}
                </span>
              </Button>


            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: '#9ca3af',
                  fontSize: '0.8rem',
                }}
              >
                © {new Date().getFullYear()}  A1 Real Estate PVT LTD
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
            Forgot Password
          </Typography>
        </DialogTitle>
        <DialogContent>
          {forgotPasswordSuccess ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  animation: 'scaleIn 0.5s ease-in-out',
                  '@keyframes scaleIn': {
                    '0%': {
                      transform: 'scale(0)',
                      opacity: 0,
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <CheckCircle
                  sx={{
                    fontSize: 80,
                    color: '#4caf50',
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  mb: 1,
                }}
              >
                Password Reset Successful!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  maxWidth: '400px',
                }}
              >
                Your password has been reset successfully. You can now login with your new password.
              </Typography>
            </Box>
          ) : forgotPasswordStep === 1 ? (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Enter your phone number to receive an OTP.
              </Typography>
              {forgotPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {forgotPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="forgot-phone"
                label="Phone Number"
                type="tel"
                fullWidth
                value={forgotPasswordPhone}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setForgotPasswordPhone(val);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !resendOtpLoading) {
                    handleResendOtp();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <PhoneOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Enter the OTP sent to your phone and set your new password.
              </Typography>
              {forgotPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {forgotPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="forgot-otp"
                label="OTP"
                type="text"
                fullWidth
                value={forgotPasswordOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setForgotPasswordOtp(value);
                }}
                InputProps={{
                  startAdornment: (
                    <VpnKeyOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
                placeholder="Enter 6-digit OTP"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 1 }}>
                {resendOtpCooldown > 0 ? (
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Resend OTP in {resendOtpCooldown}s
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendOtp}
                    disabled={resendOtpLoading}
                    sx={{
                      color: resendOtpLoading ? '#999' : '#667eea',
                      textDecoration: 'none',
                      cursor: resendOtpLoading ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: resendOtpLoading ? 'none' : 'underline',
                      },
                    }}
                  >
                    {resendOtpLoading ? 'Sending...' : 'Resend OTP'}
                  </Link>
                )}
              </Box>

              <Input
                margin="dense"
                id="new-password"
                label="New Password"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !forgotPasswordLoading) {
                    handleResetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: '#667eea' }}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <Input
                margin="dense"
                id="confirm-password"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !forgotPasswordLoading) {
                    handleResetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: '#667eea' }}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          {!forgotPasswordSuccess && (
            <>
              <MuiButton
                onClick={forgotPasswordStep === 2 ? () => setForgotPasswordStep(1) : handleCloseForgotPassword}
                sx={{
                  color: '#666',
                  textTransform: 'none',
                }}
              >
                {forgotPasswordStep === 2 ? 'Back' : 'Cancel'}
              </MuiButton>
              {forgotPasswordStep === 1 ? (
                <MuiButton
                  onClick={handleResendOtp}
                  disabled={resendOtpLoading || !forgotPasswordPhone}
                  variant="contained"
                  sx={{
                    backgroundColor: 'primary.main',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    '&:disabled': {
                      background: '#ccc',
                    },
                  }}
                >
                  {resendOtpLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Send OTP'
                  )}
                </MuiButton>
              ) : (
                <MuiButton
                  onClick={handleResetPassword}
                  disabled={forgotPasswordLoading || !newPassword || !confirmPassword}
                  variant="contained"
                  sx={{
                    backgroundColor: 'primary.main',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    '&:disabled': {
                      background: '#ccc',
                    },
                  }}
                >
                  {forgotPasswordLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Reset Password'
                  )}
                </MuiButton>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Set Password Dialog */}
      <Dialog
        open={setPasswordOpen}
        onClose={handleCloseSetPassword}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
            Set Password
          </Typography>
        </DialogTitle>
        <DialogContent>
          {setPasswordSuccess ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  mb: 2,
                  animation: 'scaleIn 0.5s ease-in-out',
                  '@keyframes scaleIn': {
                    '0%': {
                      transform: 'scale(0)',
                      opacity: 0,
                    },
                    '50%': {
                      transform: 'scale(1.2)',
                    },
                    '100%': {
                      transform: 'scale(1)',
                      opacity: 1,
                    },
                  },
                }}
              >
                <CheckCircle
                  sx={{
                    fontSize: 80,
                    color: '#4caf50',
                  }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#333',
                  mb: 1,
                }}
              >
                Password Set Successfully!
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  maxWidth: '400px',
                }}
              >
                Your password has been set successfully. You can now login with your new password.
              </Typography>
            </Box>
          ) : setPasswordStep === 1 ? (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Enter your email address to receive an OTP for setting your password.
              </Typography>
              {setPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {setPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="set-password-email"
                label="Email Address"
                type="email"
                fullWidth
                value={setPasswordEmail}
                onChange={(e) => setSetPasswordEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleVerifySetPasswordOtp();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <EmailOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </>
          ) : setPasswordStep === 2 ? (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Enter the 4-6 digit OTP sent to your email.
              </Typography>
              {setPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {setPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="set-password-otp"
                label="OTP"
                type="text"
                fullWidth
                value={setPasswordOtp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setSetPasswordOtp(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleVerifySetPasswordOtp();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <VpnKeyOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
                placeholder="Enter OTP"
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, mb: 1 }}>
                {resendSetPasswordOtpCooldown > 0 ? (
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                    Resend OTP in {resendSetPasswordOtpCooldown}s
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendSetPasswordOtp}
                    disabled={resendSetPasswordOtpLoading || !setPasswordEmail}
                    sx={{
                      color: resendSetPasswordOtpLoading || !setPasswordEmail ? '#999' : '#667eea',
                      textDecoration: 'none',
                      cursor: resendSetPasswordOtpLoading || !setPasswordEmail ? 'not-allowed' : 'pointer',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: resendSetPasswordOtpLoading || !setPasswordEmail ? 'none' : 'underline',
                      },
                    }}
                  >
                    {resendSetPasswordOtpLoading ? 'Sending...' : 'Resend OTP'}
                  </Link>
                )}
              </Box>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                Enter your new password and confirm it.
              </Typography>
              {setPasswordError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {setPasswordError}
                </Alert>
              )}
              <Input
                autoFocus
                margin="dense"
                id="set-new-password"
                label="New Password"
                type={showSetPasswordNewPassword ? 'text' : 'password'}
                fullWidth
                value={setPasswordNewPassword}
                onChange={(e) => setSetPasswordNewPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleSetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowSetPasswordNewPassword(!showSetPasswordNewPassword)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: '#667eea' }}
                      >
                        {showSetPasswordNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
              <Input
                margin="dense"
                id="set-confirm-password"
                label="Confirm Password"
                type={showSetPasswordConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={setPasswordConfirmPassword}
                onChange={(e) => setSetPasswordConfirmPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !setPasswordLoading) {
                    handleSetPassword();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <LockOutlined sx={{ color: '#667eea', mr: 1 }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowSetPasswordConfirmPassword(!showSetPasswordConfirmPassword)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                        sx={{ color: '#667eea' }}
                      >
                        {showSetPasswordConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          {!setPasswordSuccess && (
            <>
              <MuiButton
                onClick={setPasswordStep === 1 ? handleCloseSetPassword : () => setSetPasswordStep(setPasswordStep - 1)}
                sx={{
                  color: '#666',
                  textTransform: 'none',
                }}
              >
                {setPasswordStep === 1 ? 'Cancel' : 'Back'}
              </MuiButton>
              {setPasswordStep === 1 ? (
                <MuiButton
                  onClick={handleVerifySetPasswordOtp}
                  disabled={setPasswordLoading || !setPasswordEmail}
                  variant="contained"
                  sx={{
                    backgroundColor: 'primary.main',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    '&:disabled': {
                      background: '#ccc',
                    },
                  }}
                >
                  {setPasswordLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Send OTP'
                  )}
                </MuiButton>
              ) : setPasswordStep === 2 ? (
                <MuiButton
                  onClick={handleVerifySetPasswordOtp}
                  disabled={setPasswordLoading || !setPasswordOtp}
                  variant="contained"
                  sx={{
                    backgroundColor: 'primary.main',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    '&:disabled': {
                      background: '#ccc',
                    },
                  }}
                >
                  {setPasswordLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Verify OTP'
                  )}
                </MuiButton>
              ) : (
                <MuiButton
                  onClick={handleSetPassword}
                  disabled={setPasswordLoading || !setPasswordNewPassword || !setPasswordConfirmPassword}
                  variant="contained"
                  sx={{
                    backgroundColor: 'primary.main',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    '&:disabled': {
                      background: '#ccc',
                    },
                  }}
                >
                  {setPasswordLoading ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : (
                    'Set Password'
                  )}
                </MuiButton>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// New login handler using AuthContext and /api/login
async function handleLoginInner({
  auth,
  userDispatch,
  loginValue,
  passwordValue,
  history,
  setIsLoading,
  setError,
}) {
  try {
    setError(null);
    setIsLoading(true);
    const token = await auth.login({ loginId: loginValue, password: passwordValue });
    try {
      const claims = auth.user || null;
      if (claims) {
        localStorage.setItem('user', JSON.stringify(claims));
      }
    } catch { }
    // Keep existing UserContext expectations: store token and init
    // receiveToken(token, userDispatch, history);
    // doInit()(userDispatch);
    receiveToken(token, userDispatch); // NO history here
    doInit()(userDispatch);
    // wait for auth state → then navigate
    setTimeout(() => {
      history.push("/dashboard");
    }, 0);

  } catch (e) {
    console.log(e);
    // Extract error message from API response
    let errorMessage = 'Something is wrong with your login or password.';

    if (e?.response?.data?.message) {
      errorMessage = e.response.data.message;
    } else if (e?.message) {
      errorMessage = e.message;
    }

    setError(errorMessage);
  } finally {
    setIsLoading(false);
  }
}

export default withRouter(Login);
